<template>
  <div class="highlight-workspace">
    <div v-if="analyzing" class="segment-guard-sticky">
      <i class="el-icon-warning-outline segment-guard-sticky__icon"></i>
      <div class="segment-guard-sticky__body">
        <strong>精彩分析进行中，请勿刷新或关闭页面</strong>
        <p>
          提交后将占用今日任务次数；刷新不会取消后台任务，但可能导致进度无法显示。
        </p>
      </div>
    </div>

    <div class="page-header">
      <h2>发现主播精彩</h2>
      <p>
        上传直播/录播长视频（可选弹幕），发现精彩片段。
        <el-tag v-if="useMock" size="mini" type="warning">演示模式（Mock）</el-tag>
        <el-tag v-else-if="backendOnline" size="mini" type="success">后端已连接</el-tag>
        <el-tag v-else size="mini" type="danger">后端未连接</el-tag>
      </p>
    </div>

    <el-alert
      :title="uploadAlertTitle"
      type="info"
      :closable="false"
      show-icon
      class="tips-alert"
    />
    <el-alert
      title="结果为候选建议，请预览后再发布。无弹幕也能分析；有弹幕可提升互动热度权重。"
      type="warning"
      :closable="false"
      show-icon
      class="tips-alert"
    />
    <el-alert
      v-if="!useMock"
      :title="retentionTip"
      type="warning"
      :closable="false"
      show-icon
      class="tips-alert"
    />

    <div class="uploader-wrap" :class="{ 'is-locked': analyzing, 'has-footage': !!currentFootage }">
      <el-upload
        class="uploader"
        action="#"
        :http-request="handleCustomUpload"
        :before-upload="beforeUpload"
        :show-file-list="false"
        :disabled="uploading || analyzing"
        drag
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将<em>直播回放</em>拖到此处，或点击上传</div>
        <div slot="tip" class="el-upload__tip">{{ uploadTipText }}</div>
      </el-upload>
      <div v-if="analyzing" class="uploader-lock-overlay">
        <i class="el-icon-loading"></i>
        <span>精彩分析中，请勿刷新</span>
      </div>
    </div>

    <div v-if="!useMock" class="reuse-bar">
      <el-button
        size="small"
        icon="el-icon-folder-opened"
        :disabled="uploading || analyzing"
        @click="openRecentProjects"
      >
        选用已上传视频
      </el-button>
      <span class="reuse-hint">同一条回放可反复「发现精彩」，无需重新上传（文件约保留 {{ retentionHours }} 小时）</span>
    </div>

    <div v-if="uploading" class="progress-container">
      <el-progress
        class="progress-line-full"
        :percentage="uploadPercentage"
        :status="uploadPercentage === 100 ? 'success' : null"
      />
      <span class="progress-hint">正在上传视频…</span>
    </div>

    <div v-if="currentFootage" class="footage-panel">
      <h3>当前视频</h3>
      <el-descriptions :column="2" border size="small">
        <el-descriptions-item label="文件名">{{ currentFootage.name }}</el-descriptions-item>
        <el-descriptions-item label="项目 ID">{{ currentFootage.projectId }}</el-descriptions-item>
        <el-descriptions-item label="时长">{{ currentFootage.durationSeconds }} 秒</el-descriptions-item>
        <el-descriptions-item label="分辨率">
          {{ currentFootage.width || '-' }} × {{ currentFootage.height || '-' }}
        </el-descriptions-item>
      </el-descriptions>
      <video
        v-if="currentFootage.previewUrl"
        class="source-preview"
        :src="currentFootage.previewUrl"
        controls
        preload="metadata"
      />
    </div>

    <div v-if="currentFootage" class="segment-section">
      <h3 class="section-title">分析参数</h3>
      <el-form label-width="100px" size="small" class="param-form">
        <el-form-item label="直播类型">
          <el-radio-group v-model="liveCategory" :disabled="analyzing">
            <el-radio-button
              v-for="item in liveCategories"
              :key="item.value"
              :label="item.value"
            >
              {{ item.label }}
            </el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="片段时长">
          <el-radio-group v-model="selectedDuration" :disabled="analyzing" class="duration-group">
            <el-radio-button
              v-for="item in highlightDurations"
              :key="String(item.value)"
              :label="item.value"
            >
              {{ item.label }}
            </el-radio-button>
          </el-radio-group>
          <p class="field-hint">
            {{ durationHint }}
          </p>
        </el-form-item>
        <el-form-item label="候选条数">
          <el-select v-model="candidateCount" :disabled="analyzing" style="width: 140px">
            <el-option
              v-for="item in candidateOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="弹幕文件">
          <div class="danmaku-row">
            <el-upload
              action="#"
              :show-file-list="false"
              :disabled="analyzing"
              accept=".xml,.txt"
              :http-request="handleDanmakuUpload"
            >
              <el-button size="mini" icon="el-icon-document" :disabled="analyzing">
                {{ danmakuObjectKey ? '重新上传弹幕' : '可选：上传弹幕' }}
              </el-button>
            </el-upload>
            <span v-if="danmakuFileName" class="danmaku-name">{{ danmakuFileName }}</span>
            <el-button
              v-if="danmakuObjectKey"
              type="text"
              size="mini"
              :disabled="analyzing"
              @click="clearDanmaku"
            >
              清除
            </el-button>
          </div>
          <p class="field-hint">支持 B 站 XML 或每行一个时间戳（秒）的 TXT；不上传也能分析。</p>
        </el-form-item>
        <el-form-item label="智能封面">
          <div class="smart-cover-row">
            <el-switch v-model="coverEnabled" :disabled="analyzing" />
            <span class="smart-cover-desc">开启后按各片段情绪峰值帧生成竖屏封面图（1080×1920）</span>
          </div>
        </el-form-item>
      </el-form>

      <segment-watermark-panel
        :enabled.sync="watermarkEnabled"
        :text.sync="watermarkText"
        :text-color.sync="watermarkTextColor"
        :font-size.sync="watermarkFontSize"
        :position.sync="watermarkPosition"
        :text-stroke.sync="watermarkTextStroke"
        :text-stroke-color.sync="watermarkTextStrokeColor"
        :disabled="analyzing"
      />
      <segment-publish-panel
        :enabled.sync="publishEnabled"
        :hashtags.sync="publishHashtags"
        :description-template.sync="publishDescriptionTemplate"
        :disabled="analyzing"
      />

      <div class="segment-actions">
        <el-button
          type="primary"
          icon="el-icon-magic-stick"
          :loading="analyzing"
          :disabled="!canStartAnalyze"
          @click="handleAnalyze"
        >
          发现精彩
        </el-button>
        <el-button v-if="segmentResults.length" :disabled="analyzing" @click="clearResults">
          清空结果
        </el-button>
        <el-button :disabled="uploading || analyzing" @click="resetWorkspace">
          清空当前
        </el-button>
      </div>

      <div v-if="analyzing" class="segment-progress-panel">
        <div class="segment-progress-panel__title">
          <i class="el-icon-warning"></i>
          <span>精彩分析进行中</span>
        </div>
        <p class="segment-progress-panel__hint">
          {{ displayAnalyzingHint }}
        </p>
        <p v-if="analyzingRotateTip" class="segment-progress-panel__subhint">
          {{ analyzingRotateTip }}
        </p>
        <el-progress class="progress-line-full" :percentage="segmentProgress" :stroke-width="14" />
      </div>
    </div>

    <segment-result-list
      v-if="segmentResults.length"
      title="精彩候选"
      :hint="resultListHint"
      :clips="segmentResults"
      :requested-count="candidateCount"
      :playing-clip="playingClip"
      :tuneable="true"
      :feedbackable="true"
      :live-category="liveCategory"
      :source-duration-seconds="currentFootage ? currentFootage.durationSeconds : 0"
      :refining-id="refiningSegmentId"
      @play="openPlayer"
      @close-player="playingClip = null"
      @download-one="downloadSingle"
      @batch-download="downloadBatch"
      @selection-change="onSelectionChange"
      @remove-clip="removeExpiredClip"
      @refine="handleRefine"
      @feedback="handleSegmentFeedback"
    />
    <segment-publish-assistant
      v-if="segmentResults.length && publishEnabled"
      :task-id="segmentTaskId"
      :clips="segmentResults"
      :selected-clips="selectedClips"
      :cover-title="'精彩'"
      :publish-hashtags.sync="publishHashtags"
      :publish-description-template.sync="publishDescriptionTemplate"
      :use-mock="useMock"
      :fallback-file-name="publishPackFileName"
    />

    <el-dialog
      title="选用已上传视频"
      :visible.sync="recentDialogVisible"
      width="720px"
      append-to-body
      @open="loadRecentProjects"
    >
      <p class="recent-dialog-hint">
        选择后可直接再点「发现精彩」。不会重新上传；每次分析仍会占用任务次数。
      </p>
      <el-table
        v-loading="recentLoading"
        :data="recentProjects"
        size="small"
        empty-text="暂无已上传视频"
        max-height="360"
      >
        <el-table-column prop="fileName" label="文件名" min-width="220" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.fileName || row.name || row.projectId }}
          </template>
        </el-table-column>
        <el-table-column label="时长" width="90">
          <template slot-scope="{ row }">
            {{ formatDurationClock(row.durationSeconds) }}
          </template>
        </el-table-column>
        <el-table-column label="上传时间" width="160">
          <template slot-scope="{ row }">
            {{ formatCreatedAt(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              type="primary"
              size="mini"
              :loading="selectingProjectId === row.projectId"
              @click="selectRecentProject(row)"
            >
              选用
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import SegmentWatermarkPanel from './SegmentWatermarkPanel.vue';
import SegmentPublishPanel from './SegmentPublishPanel.vue';
import SegmentPublishAssistant from './SegmentPublishAssistant.vue';
import SegmentResultList from './SegmentResultList.vue';
import { uploadVideoFile } from '../api/upload';
import {
  submitHighlightTask,
  resumeSegmentTask,
  uploadDanmakuFile,
  refineHighlightSegment,
  submitHighlightSegmentFeedback,
} from '../api/segment';
import { listProjects, getProject } from '../api/project';
import { fetchMyQuota } from '../api/auth';
import { request } from '../api/client';
import {
  API_PATHS,
  USE_MOCK,
  VIDEO_RETENTION_HOURS,
} from '../config';
import { formatFileSizeMb } from '../utils/formatQuota';
import {
  HIGHLIGHT_DURATIONS,
  HIGHLIGHT_SMART_DURATION,
  HIGHLIGHT_CANDIDATE_OPTIONS,
  DEFAULT_HIGHLIGHT_CANDIDATE_COUNT,
  LIVE_CATEGORIES,
  DEFAULT_LIVE_CATEGORY,
} from '../constants/highlightOptions';
import {
  highlightFeedbackCopy,
  feedbackTypeLabel,
} from '../constants/highlightFeedbackCopy';
import { WATERMARK_POSITIONS } from '../constants/watermarkPositions';
import { WATERMARK_FONT_SIZE_DEFAULT } from '../constants/textFontSizes';
import { validateVideoFile } from '../utils/videoValidate';
import { downloadFile, downloadFilesBatch, downloadSegmentBatchZip } from '../utils/download';
import { loadWorkspaceState, saveWorkspaceState, clearWorkspaceState } from '../utils/workspaceStorage';

const HIGHLIGHT_STORAGE_KEY = 'aivideoclip.highlight.workspace';

/** 分析进行中底部轮播，减轻「文案不动=卡住」的错觉 */
const ANALYZING_ROTATE_TIPS = [
  '长视频解析需要几分钟，请耐心等待',
  '后台仍在处理，请勿刷新或关闭页面',
  '正在全片扫描，稍后会进入切片导出',
  '进度会随阶段更新，短暂停顿属正常',
  '大文件会更久一些，并非卡住',
];

const PUBLISH_PRESETS = {
  GAME: {
    hashtags: '#游戏高光 #直播剪辑 #精彩瞬间',
    descriptionTemplate: '高光片段 {index}：{title}',
  },
  SHOW: {
    hashtags: '#直播剪辑 #聊天互动 #主播日常',
    descriptionTemplate: '互动片段 {index}：{title}',
  },
  SING: {
    hashtags: '#唱歌直播 #副歌高光 #好听',
    descriptionTemplate: '演唱片段 {index}：{title}',
  },
  DANCE: {
    hashtags: '#舞蹈直播 #跳舞高光 #好看',
    descriptionTemplate: '舞蹈片段 {index}：{title}',
  },
};

export default {
  name: 'HighlightWorkspace',
  components: {
    SegmentWatermarkPanel,
    SegmentPublishPanel,
    SegmentPublishAssistant,
    SegmentResultList,
  },
  data() {
    return {
      useMock: USE_MOCK,
      backendOnline: false,
      quotaLimits: null,
      uploading: false,
      uploadPercentage: 0,
      analyzing: false,
      segmentProgress: 0,
      segmentStatusHint: '',
      analyzingTipIndex: 0,
      analyzingTipTimer: null,
      liveCategories: LIVE_CATEGORIES,
      liveCategory: DEFAULT_LIVE_CATEGORY,
      highlightDurations: HIGHLIGHT_DURATIONS,
      selectedDuration: HIGHLIGHT_SMART_DURATION,
      candidateOptions: HIGHLIGHT_CANDIDATE_OPTIONS,
      candidateCount: DEFAULT_HIGHLIGHT_CANDIDATE_COUNT,
      danmakuObjectKey: null,
      danmakuFileName: '',
      coverEnabled: true,
      watermarkEnabled: false,
      watermarkText: '',
      watermarkTextColor: '#FFFFFF',
      watermarkFontSize: WATERMARK_FONT_SIZE_DEFAULT,
      watermarkPosition: WATERMARK_POSITIONS.BOTTOM_RIGHT,
      watermarkTextStroke: true,
      watermarkTextStrokeColor: '#000000',
      publishEnabled: false,
      publishHashtags: PUBLISH_PRESETS.GAME.hashtags,
      publishDescriptionTemplate: PUBLISH_PRESETS.GAME.descriptionTemplate,
      selectedClips: [],
      currentFootage: null,
      segmentResults: [],
      segmentTaskId: null,
      playingClip: null,
      refiningSegmentId: '',
      recentDialogVisible: false,
      recentLoading: false,
      recentProjects: [],
      selectingProjectId: '',
      retentionHours: VIDEO_RETENTION_HOURS,
    };
  },
  computed: {
    canStartAnalyze() {
      if (!this.currentFootage || this.uploading || this.analyzing) {
        return false;
      }
      if (this.watermarkEnabled && !this.watermarkText.trim()) {
        return false;
      }
      return true;
    },
    durationHint() {
      if (this.selectedDuration === HIGHLIGHT_SMART_DURATION) {
        if (this.liveCategory === 'SHOW') {
          return '自动识别聊天/互动等高光（约 15~90 秒），更看重弹幕与笑声、反应；无弹幕时仍可按语音起伏分析';
        }
        if (this.liveCategory === 'SING') {
          return '自动识别整首演唱区间（约 2~5 分钟），优先完整一首并尽量覆盖多首歌；避免半首凑数';
        }
        if (this.liveCategory === 'DANCE') {
          return '自动识别舞蹈成段（暂与唱歌相同策略，约 2~5 分钟），尽量覆盖多段；后续会单独调优';
        }
        return '自动识别整场高燃（约 25 秒~3 分钟），更看重大叫与紧凑喊麦；不够精彩时少返回';
      }
      const found = this.highlightDurations.find(item => item.value === this.selectedDuration);
      return found ? found.hint : '';
    },
    resultListHint() {
      return highlightFeedbackCopy(this.liveCategory).listHint;
    },
    displayAnalyzingHint() {
      return this.segmentStatusHint || '正在分析声音与切镜…';
    },
    analyzingRotateTip() {
      if (!this.analyzing) {
        return '';
      }
      return ANALYZING_ROTATE_TIPS[this.analyzingTipIndex % ANALYZING_ROTATE_TIPS.length];
    },
    retentionTip() {
      return `服务器上的原视频与片段文件约保留 ${this.retentionHours} 小时，到期后自动清理。请及时下载。`;
    },
    uploadAlertTitle() {
      if (!this.quotaLimits) {
        return '支持 MP4 / MKV / MOV 等，上传限制按当前账号档位';
      }
      const size = formatFileSizeMb(this.quotaLimits.maxFileMb);
      return `支持 MP4 / MKV / MOV 等，当前档位单文件最大 ${size}，时长 ${this.quotaLimits.maxDurationMin} 分钟`;
    },
    uploadTipText() {
      if (!this.quotaLimits) {
        return '单文件与时长上限按当前账号档位限制';
      }
      const size = formatFileSizeMb(this.quotaLimits.maxFileMb);
      return `单文件不超过 ${size}，视频时长不超过 ${this.quotaLimits.maxDurationMin} 分钟`;
    },
    publishPackFileName() {
      const baseName = this.currentFootage && this.currentFootage.name
        ? this.currentFootage.name.replace(/\.[^.]+$/, '')
        : 'highlight';
      const count = this.selectedClips.length;
      if (count > 0 && count < this.segmentResults.length) {
        return `${baseName}_publish_pack_${count}.zip`;
      }
      return `${baseName}_publish_pack.zip`;
    },
  },
  mounted() {
    this.checkBackendHealth();
    this.loadUserQuota();
    this.restoreWorkspaceState();
  },
  watch: {
    analyzing(val) {
      this.syncPageLeaveGuard();
      if (val) {
        this.startAnalyzingTipRotate();
      } else {
        this.stopAnalyzingTipRotate();
      }
    },
    uploading() {
      this.syncPageLeaveGuard();
    },
    liveCategory(value) {
      const preset = PUBLISH_PRESETS[value] || PUBLISH_PRESETS.GAME;
      this.publishHashtags = preset.hashtags;
      this.publishDescriptionTemplate = preset.descriptionTemplate;
    },
  },
  beforeDestroy() {
    this.stopAnalyzingTipRotate();
    this.revokePreviewUrl();
    this.removePageLeaveGuard();
  },
  methods: {
    startAnalyzingTipRotate() {
      this.stopAnalyzingTipRotate();
      this.analyzingTipIndex = 0;
      this.analyzingTipTimer = setInterval(() => {
        this.analyzingTipIndex += 1;
      }, 10000);
    },
    stopAnalyzingTipRotate() {
      if (this.analyzingTipTimer) {
        clearInterval(this.analyzingTipTimer);
        this.analyzingTipTimer = null;
      }
      this.analyzingTipIndex = 0;
    },
    syncPageLeaveGuard() {
      if (this.analyzing || this.uploading) {
        window.addEventListener('beforeunload', this.onPageLeaveWarning);
      } else {
        this.removePageLeaveGuard();
      }
    },
    removePageLeaveGuard() {
      window.removeEventListener('beforeunload', this.onPageLeaveWarning);
    },
    onPageLeaveWarning(event) {
      event.preventDefault();
      event.returnValue = '';
    },
    async loadUserQuota() {
      if (USE_MOCK) return;
      try {
        const data = await fetchMyQuota();
        this.quotaLimits = data.limits;
      } catch (e) {
        // ignore
      }
    },
    async checkBackendHealth() {
      if (USE_MOCK) {
        this.backendOnline = true;
        return;
      }
      try {
        await request(API_PATHS.health);
        this.backendOnline = true;
      } catch (e) {
        this.backendOnline = false;
      }
    },
    beforeUpload(file) {
      const result = validateVideoFile(file, {
        maxFileMb: this.quotaLimits && this.quotaLimits.maxFileMb,
      });
      if (!result.ok) {
        this.$message.error(result.message);
        return false;
      }
      return true;
    },
    async handleCustomUpload({ file }) {
      this.uploading = true;
      this.uploadPercentage = 0;
      this.segmentResults = [];
      this.segmentTaskId = null;
      this.clearDanmaku();
      try {
        const data = await uploadVideoFile(file, (evt) => {
          this.uploadPercentage = Math.round(evt.percent || 0);
        });
        this.setCurrentFootage(file, data);
        saveWorkspaceState({ projectId: data.projectId, taskId: null }, HIGHLIGHT_STORAGE_KEY);
        this.$message.success('上传完成');
      } catch (e) {
        this.$message.error(e.message || '上传失败');
      } finally {
        this.uploading = false;
      }
    },
    setCurrentFootage(file, data) {
      this.revokePreviewUrl();
      const previewUrl = data.url || (file ? URL.createObjectURL(file) : data.previewUrl);
      this.currentFootage = {
        name: data.fileName || (file && file.name) || data.name || 'video',
        projectId: data.projectId,
        videoId: data.videoId,
        durationSeconds: data.durationSeconds,
        width: data.width,
        height: data.height,
        previewUrl,
      };
    },
    applyProjectDetail(detail) {
      this.revokePreviewUrl();
      this.currentFootage = {
        name: detail.fileName || detail.name || 'video',
        projectId: detail.projectId,
        videoId: detail.videoId,
        durationSeconds: detail.durationSeconds,
        width: detail.width,
        height: detail.height,
        previewUrl: detail.previewUrl,
      };
      this.clearResults();
      this.clearDanmaku();
      saveWorkspaceState({ projectId: detail.projectId, taskId: null }, HIGHLIGHT_STORAGE_KEY);
    },
    openRecentProjects() {
      this.recentDialogVisible = true;
    },
    async loadRecentProjects() {
      this.recentLoading = true;
      try {
        const data = await listProjects({ page: 0, size: 30 });
        this.recentProjects = data.items || [];
      } catch (e) {
        this.recentProjects = [];
        this.$message.error(e.message || '加载已上传视频失败');
      } finally {
        this.recentLoading = false;
      }
    },
    async selectRecentProject(row) {
      if (!row || !row.projectId) return;
      this.selectingProjectId = row.projectId;
      try {
        const detail = await getProject(row.projectId);
        this.applyProjectDetail(detail);
        this.recentDialogVisible = false;
        this.$message.success('已选用该视频，可直接点「发现精彩」');
      } catch (e) {
        this.$message.error(e.message || '选用失败，文件可能已过期清理');
      } finally {
        this.selectingProjectId = '';
      }
    },
    formatDurationClock(totalSeconds) {
      const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      }
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    formatCreatedAt(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const y = date.getFullYear();
      const mo = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const mi = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${mo}-${d} ${h}:${mi}`;
    },
    revokePreviewUrl() {
      if (this.currentFootage && this.currentFootage.previewUrl
        && this.currentFootage.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.currentFootage.previewUrl);
      }
    },
    async handleDanmakuUpload({ file }) {
      if (!this.currentFootage) return;
      try {
        const data = await uploadDanmakuFile(this.currentFootage.projectId, file);
        this.danmakuObjectKey = data.objectKey;
        this.danmakuFileName = file.name;
        this.$message.success('弹幕已上传');
      } catch (e) {
        this.$message.error(e.message || '弹幕上传失败');
      }
    },
    clearDanmaku() {
      this.danmakuObjectKey = null;
      this.danmakuFileName = '';
    },
    async handleAnalyze() {
      if (!this.currentFootage) return;
      if (this.watermarkEnabled && !this.watermarkText.trim()) {
        this.$message.warning('请填写水印文字');
        return;
      }
      this.analyzing = true;
      this.segmentProgress = 0;
      this.segmentStatusHint = '提交任务…';
      this.$message({
        type: 'warning',
        message: '精彩分析已开始，请勿刷新或关闭页面',
        duration: 5000,
        showClose: true,
      });
      try {
        const result = await submitHighlightTask(
          this.currentFootage.projectId,
          {
            durationSeconds: this.selectedDuration,
            candidateCount: this.candidateCount,
            liveCategory: this.liveCategory,
            danmakuObjectKey: this.danmakuObjectKey,
            cover: { enabled: this.coverEnabled },
            watermark: this.watermarkEnabled
              ? {
                  enabled: true,
                  text: this.watermarkText.trim(),
                  textColor: this.watermarkTextColor,
                  fontSize: this.watermarkFontSize,
                  position: this.watermarkPosition,
                  textStroke: this.watermarkTextStroke,
                  textStrokeColor: this.watermarkTextStrokeColor,
                }
              : null,
            publish: this.publishEnabled
              ? {
                  enabled: true,
                  hashtags: this.publishHashtags,
                  descriptionTemplate: this.publishDescriptionTemplate,
                }
              : null,
          },
          {
            onProgress: (evt) => {
              const next = evt.progressPercent || 0;
              this.segmentProgress = Math.max(this.segmentProgress, next);
              this.segmentStatusHint = evt.progressMessage || evt.status;
            },
          },
        );
        this.applyTaskResult(result);
        const got = this.segmentResults.length;
        if (got < this.candidateCount) {
          this.$message.success(
            `分析完成，共 ${got} 个精彩片段（目标 ${this.candidateCount} 条；重合或质量不足时会少一些）`,
          );
        } else {
          this.$message.success(`分析完成，共 ${got} 个精彩片段`);
        }
        this.$emit('quota-refresh');
      } catch (e) {
        this.$message.error(e.message || '精彩分析失败');
      } finally {
        this.analyzing = false;
      }
    },
    applyTaskResult(result) {
      this.segmentResults = result.segments || [];
      this.segmentTaskId = result.taskId || null;
      this.publishHashtags = result.publishHashtags || this.publishHashtags;
      this.publishDescriptionTemplate = result.publishDescriptionTemplate
        || this.publishDescriptionTemplate;
      if (result.publishHashtags || result.publishDescriptionTemplate) {
        this.publishEnabled = true;
      }
      this.selectedClips = [];
      saveWorkspaceState({
        projectId: this.currentFootage && this.currentFootage.projectId,
        taskId: this.segmentTaskId,
      }, HIGHLIGHT_STORAGE_KEY);
    },
    async handleRefine({ clip, startMs, endMs }) {
      if (!this.segmentTaskId || !clip || !clip.id) {
        this.$message.warning('无法微调：缺少任务或片段 ID');
        return;
      }
      this.refiningSegmentId = clip.id;
      try {
        const updated = await refineHighlightSegment(this.segmentTaskId, clip.id, { startMs, endMs });
        this.segmentResults = this.segmentResults.map((item) => {
          if (item.id !== clip.id) {
            return item;
          }
          return {
            ...item,
            ...updated,
            coverEnabled: updated.coverEnabled != null ? updated.coverEnabled : item.coverEnabled,
            score: updated.score != null ? updated.score : item.score,
            peakSecond: updated.peakSecond != null ? updated.peakSecond : item.peakSecond,
            reasonLabel: updated.reasonLabel != null ? updated.reasonLabel : item.reasonLabel,
            completenessLabel: updated.completenessLabel != null
              ? updated.completenessLabel
              : item.completenessLabel,
            userRefined: updated.userRefined != null ? updated.userRefined : true,
          };
        });
        this.$message.success('已按新区间重切');
      } catch (e) {
        this.$message.error(e.message || '微调失败');
      } finally {
        this.refiningSegmentId = '';
      }
    },
    async handleSegmentFeedback({ clip, feedbackType }) {
      if (!this.segmentTaskId || !clip || !clip.id || !feedbackType) {
        this.$message.warning('无法提交反馈');
        return;
      }
      try {
        await submitHighlightSegmentFeedback(this.segmentTaskId, clip.id, { feedbackType });
        this.$message.success(`已记录：${feedbackTypeLabel(this.liveCategory, feedbackType)}`);
      } catch (e) {
        this.$message.error(e.message || '反馈提交失败');
      }
    },
    onSelectionChange(clips) {
      this.selectedClips = clips || [];
    },
    removeExpiredClip(clip) {
      const key = clip.id != null ? clip.id : clip.index;
      this.segmentResults = this.segmentResults.filter(
        item => (item.id != null ? item.id : item.index) !== key,
      );
      if (this.playingClip && (this.playingClip.id != null ? this.playingClip.id : this.playingClip.index) === key) {
        this.playingClip = null;
      }
      this.selectedClips = this.selectedClips.filter(
        item => (item.id != null ? item.id : item.index) !== key,
      );
    },
    async resumeRunningTask(taskId) {
      this.analyzing = true;
      try {
        const result = await resumeSegmentTask(taskId, {
          onProgress: (evt) => {
            const next = evt.progressPercent || 0;
            this.segmentProgress = Math.max(this.segmentProgress, next);
            this.segmentStatusHint = evt.progressMessage || evt.status;
          },
        });
        this.applyTaskResult(result);
      } catch (e) {
        this.$message.error(e.message || '恢复任务失败');
      } finally {
        this.analyzing = false;
      }
    },
    async restoreWorkspaceState() {
      if (USE_MOCK) return;
      const state = loadWorkspaceState(HIGHLIGHT_STORAGE_KEY);
      if (!state) return;
      if (state.projectId && !this.currentFootage) {
        try {
          const detail = await getProject(state.projectId);
          this.applyProjectDetail(detail);
        } catch (e) {
          // 项目可能已清理
        }
      }
      if (state.taskId) {
        try {
          await this.resumeRunningTask(state.taskId);
        } catch (e) {
          // ignore
        }
      }
    },
    openPlayer(clip) {
      this.playingClip = clip;
    },
    async downloadSingle(clip) {
      try {
        await downloadFile(clip.url, clip.fileName || `highlight_${clip.index}.mp4`);
      } catch (e) {
        this.$message.error(e.message || '下载失败');
      }
    },
    async downloadBatch(clips) {
      const list = clips && clips.length ? clips : this.segmentResults;
      if (!list.length) return;
      try {
        if (this.segmentTaskId && !USE_MOCK) {
          await downloadSegmentBatchZip(this.segmentTaskId, list);
        } else {
          await downloadFilesBatch(list);
        }
      } catch (e) {
        this.$message.error(e.message || '批量下载失败');
      }
    },
    clearResults() {
      this.segmentResults = [];
      this.segmentTaskId = null;
      this.selectedClips = [];
      this.playingClip = null;
    },
    resetWorkspace() {
      this.revokePreviewUrl();
      this.currentFootage = null;
      this.clearResults();
      this.clearDanmaku();
      clearWorkspaceState(HIGHLIGHT_STORAGE_KEY);
    },
  },
};
</script>

<style scoped>
.highlight-workspace {
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  padding: 20px 22px 28px;
}
.page-header h2 {
  margin: 0 0 8px;
  font-size: 20px;
  color: #f8fafc;
}
.page-header p {
  margin: 0;
  color: #94a3b8;
  line-height: 1.6;
  font-size: 14px;
}
.tips-alert {
  margin: 12px 0;
}
.segment-guard-sticky {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: -8px 0 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: rgba(245, 158, 11, 0.18);
  border: 1px solid rgba(251, 191, 36, 0.45);
  color: #fde68a;
}
.segment-guard-sticky__icon {
  font-size: 18px;
  margin-top: 2px;
}
.segment-guard-sticky__body strong {
  display: block;
  margin-bottom: 4px;
  color: #d60404;
}
.segment-guard-sticky__body p {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  /* color: #fcd34d; */
  color: #d60404;
}
.uploader-wrap {
  position: relative;
  margin: 16px 0;
  width: 100%;
}
.uploader {
  width: 100%;
}
.uploader-wrap >>> .el-upload {
  width: 100%;
  display: block;
}
.uploader-wrap >>> .el-upload-dragger {
  width: 100%;
  height: 270px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.uploader-wrap.is-locked .uploader {
  opacity: 0.55;
  pointer-events: none;
}
.uploader-lock-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(15, 23, 42, 0.55);
  color: #e2e8f0;
  border-radius: 8px;
  z-index: 2;
}
.reuse-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 0 8px;
}
.reuse-hint {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.4;
}
.recent-dialog-hint {
  margin: 0 0 12px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
}
.progress-container {
  margin-bottom: 12px;
}
.progress-hint {
  display: inline-block;
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
}
.footage-panel,
.segment-section {
  margin-top: 18px;
}
.footage-panel h3,
.section-title {
  margin: 0 0 12px;
  font-size: 16px;
  color: #e2e8f0;
}
.source-preview {
  width: 100%;
  max-height: 360px;
  margin-top: 12px;
  background: #000;
  border-radius: 8px;
}
.param-form {
  margin-bottom: 8px;
}
.danmaku-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.danmaku-name {
  color: #94a3b8;
  font-size: 12px;
}
.field-hint {
  margin: 4px 0 0;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.5;
}
.duration-group {
  display: flex;
  flex-wrap: wrap;
}
.smart-cover-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.smart-cover-desc {
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.5;
}
.segment-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin: 16px 0 8px;
}
.segment-progress-panel {
  margin-top: 14px;
  padding: 14px;
  border-radius: 8px;
  background: rgba(15, 118, 110, 0.12);
  border: 1px solid rgba(45, 212, 191, 0.25);
}
.segment-progress-panel__title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #ccfbf1;
  font-weight: 600;
  margin-bottom: 8px;
}
.segment-progress-panel__hint {
  margin: 0 0 6px;
  color: #e2e8f0;
  font-size: 13px;
  line-height: 1.5;
  min-height: 1.5em;
  transition: opacity 0.25s ease;
}
.segment-progress-panel__subhint {
  margin: 0 0 10px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}
.progress-line-full {
  width: 100%;
}
</style>
